import React, { useContext, useMemo } from 'react';
import { useMyAppointmentsLocale } from '../../pages/MyAppointments';
import { ReactComponent as Receipts } from 'assets/icons/profileSidebarIcons/receipts.svg';
import {
  AppointmentHistoryContext,
  appointmentOffsetParams,
  historyStatuses,
} from '../AppointmentHistory';
import TableContent from 'components/codexTable/TableContent';
import {
  customClassesDesktop,
  excludeSort,
  FILTER_BLOCK_ID,
  keyMapPatient,
  keyMapProvider,
  patientColumns,
  providerColumns,
} from './tableKeyMap';
import { AppointmentTypeEnum, Order_By } from 'graphql/generated/hasura';
import PaginateBlock from 'components/PaginateBlock';
import { Record_Order_By } from 'components/codexTable/interfaces/record.interfaces';
import EmptyStateComponent from 'components/EmptyStateComponent';
import PastAppointmentFilter, { IFilterForm } from './Filter';
import PastAppointmentMobileView from './PastAppointmentMobileView';
import Loader from 'components/loaderComponent';
import { twMerge } from 'tailwind-merge';
import { IMultiValueLabel } from 'components/dropdown/ReactSelectComponents';
import {
  AppointmentHistoryRowProps,
  AppointmentStatusFilterOptions,
} from '../../interfaces';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import FilterButton from 'components/button/FilterButton';
import SearchInputBlock from 'components/SearchInputBlock';
import {
  MY_ACCOUNT_PROFILE,
  MY_APPOINTMENTS_DETAILS,
} from '../../../../utilities/routes';
import ButtonComponent from '../../../../components/button/buttonComponent';
import { PATIENT_PROFILE_ROUTES } from '../../../my-account/pages/patient/constants';

const PastAppointmentWrapper = () => {
  const { locale, loading } = useMyAppointmentsLocale();
  const [openFilter, setOpenFilter] = React.useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedAccount } = useAuth();
  const isProvider = user?.role === Roles.PROVIDER;

  const columns = isProvider ? providerColumns : patientColumns;

  const keyMap = isProvider ? keyMapProvider : keyMapPatient;

  const queryParams = new URLSearchParams(location.search);

  const handleFilterClick = () => setOpenFilter((open) => !open);

  const {
    data,
    loading: dataLoading,
    total,
    setVars,
    variables,
  } = useContext(AppointmentHistoryContext);

  const historicAppointmentsOrderBy = useMemo<Record_Order_By>(() => {
    if (!variables.filter.sort || variables.filter.sortDirection === undefined)
      return {};
    return {
      [variables.filter.sort]: variables.filter.sortDirection
        ? Order_By.Asc
        : Order_By.Desc,
    };
  }, [variables]);

  const hasShowPaginate = useMemo(
    () => total > variables.limit,
    [total, variables.limit],
  );

  const getBargeFilterCount = useMemo(() => {
    const { to, appointmentStatus = [], type } = variables.filter;
    const count =
      +!!to +
      +!!type +
      +(
        !!appointmentStatus.length &&
        appointmentStatus.length !== historyStatuses.length
      );
    if (!count) {
      return null;
    }
    return String(count).padStart(2, '0');
  }, [variables.filter]);

  const setNewOffset = (offset: number) =>
    setVars((vars) => ({ ...vars, offset }));

  const handleFilterClearedOffset = () => {
    const lastQuery: Record<string, string> = {};
    queryParams.forEach((value, key) => {
      lastQuery[key] = value;
    });

    navigate({
      search: createSearchParams({
        ...lastQuery,
        [appointmentOffsetParams]: '0',
      }).toString(),
    });
  };

  const handleSortChange = (nextSort: Record_Order_By) => {
    handleFilterClearedOffset();
    return setVars((vars) => ({
      ...vars,
      offset: 0,
      filter: {
        ...vars.filter,
        sort: Object.keys(nextSort)[0],
        sortDirection:
          nextSort[Object.keys(nextSort)[0]] === Order_By.Asc ? 0 : 1,
      },
    }));
  };

  const handleOnChange = (find: string) => {
    handleFilterClearedOffset();
    return setVars((vars) => ({
      ...vars,
      offset: 0,
      filter: {
        ...vars.filter,
        find,
      },
    }));
  };

  const handleFilterChange = (filter: IFilterForm) => {
    const { to, from, appointmentStatus = [], type: typeSelect } = filter;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const type = typeSelect?.value as AppointmentTypeEnum;

    const statuses = (appointmentStatus as Array<IMultiValueLabel>)
      ?.map(({ value }) => value)
      .filter((value: string) => !!value) as AppointmentStatusFilterOptions[];

    const appointmentStatuses =
      statuses.length !== (appointmentStatus as Array<IMultiValueLabel>).length
        ? historyStatuses
        : statuses;

    handleFilterClearedOffset();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return setVars((vars) => ({
      ...vars,
      offset: 0,
      filter: {
        ...vars.filter,
        to,
        from,
        type,
        appointmentStatus: appointmentStatuses?.length
          ? appointmentStatuses
          : historyStatuses,
      },
    }));
  };

  const paginateSubtitle = useMemo(
    () =>
      `${locale?.showing} ${variables.offset + 1}-${Math.min(
        variables.offset + variables.limit,
        total,
      )} ${locale?.appointments} ${locale?.from} ${total}`,
    [locale, total, variables],
  );

  const isFilterSelected =
    variables?.filter?.find ||
    variables?.filter?.from ||
    variables?.filter?.to ||
    variables?.filter?.type ||
    variables?.filter?.appointmentStatus?.length;

  const handleRowClick = (appointment: AppointmentHistoryRowProps) => {
    return navigate(`${MY_APPOINTMENTS_DETAILS}/${appointment.id}`, {
      state: { appointmentId: appointment.id },
    });
  };

  if (!locale || loading) {
    return <Loader testID={'past-appointments-loader'} />;
  }

  if (!data?.length && !isFilterSelected && !dataLoading) {
    return (
      <div
        data-tour="past-appointments"
        className="desktop:p-8 p-5 bg-white rounded-xl flex flex-col gap-8"
      >
        <div className="flex items-center max-desktop:justify-between gap-4">
          <h3 className="desktop:text-h3 text-h4 text-neutral-800 font-semibold">
            {locale.historicAppointmentsCardTitle}
          </h3>
          <span className="text-neutral-800 text-sm font-semibold rounded-md desktop:ml-2 ml-auto px-2 py-1 bg-[rgba(0,0,0,.05)]">
            {total}
          </span>
        </div>
        <div className="desktop:px-7 flex grow justify-center items-center desktop:min-h-[400px]">
          <EmptyStateComponent
            titleClassName="desktop:font-medium text-charcoal-gray text-center max-desktop:text-h3 font-semibold"
            textClassName="text-center"
            title={
              isProvider
                ? locale?.noHistoricProviderAppointment
                : locale?.noHistoricAppointment
            }
            text={
              isProvider
                ? locale.noHistoricProviderAppointmentDescription
                : undefined
            }
            testID={'past-appointments-empty'}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'bg-white rounded-xl desktop:p-8 desktop:gap-8 p-5',
        openFilter && 'min-h-[470px]',
      )}
      data-testid="past-appointments"
      data-tour="past-appointments"
    >
      <div
        className="grid grid-cols-12 items-center gap-x-4 desktop:gap-4 mb-0.5 desktop:mb-4 relative"
        id={FILTER_BLOCK_ID}
      >
        <div className="col-span-full flex mb-[30px] desktop:mb-0">
          <h3 className="text-xl desktop:text-h3 text-neutral-800 font-semibold">
            {locale.historicAppointmentsCardTitle}
          </h3>
          <span className="text-neutral-800 text-sm font-semibold rounded-md desktop:ml-2 ml-auto px-2 py-1 bg-[rgba(0,0,0,.05)]">
            {total}
          </span>
        </div>

        <div className="flex mb-7 gap-2 desktop:gap-4 col-span-full desktop:items-center justify-between w-full">
          <SearchInputBlock
            handleOnChange={handleOnChange}
            placeholder={
              (isProvider
                ? locale.searchPatientPlaceholder
                : locale.searchPlaceholder) || 'Search by provider name'
            }
            inputClassName="placeholder:not-italic placeholder:text-med-gray-3 font-medium text-neutral-800"
            testID="search-past-appointment"
          />
          <ButtonComponent
            className={twMerge(
              'hidden',
              user?.role === Roles.PATIENT && 'ml-auto block',
              selectedAccount && 'hidden',
            )}
            Icon={Receipts}
            iconPosition="left"
            type="outlined-transparent"
            testID="receipts-history"
            onClick={() =>
              navigate(
                `${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.MY_RECEIPTS_HISTORY}`,
              )
            }
          >
            Receipts history
          </ButtonComponent>
          <FilterButton
            label={locale?.filter}
            handleFilterClick={handleFilterClick}
            isOpenFilter={openFilter}
            count={getBargeFilterCount}
            className={twMerge(
              user?.role === Roles.PATIENT && 'ml-0 !border-2',
            )}
          />
        </div>

        {openFilter && (
          <PastAppointmentFilter
            open={openFilter}
            setOpen={setOpenFilter}
            handleFilterChange={handleFilterChange}
            defaultFilter={variables.filter}
          />
        )}
      </div>
      <div className="relative">
        {dataLoading && (
          <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
            <Loader testID={'past-appointments-loader'} />
          </div>
        )}

        <div className={twMerge(dataLoading && 'opacity-50')}>
          <div className="hidden max-desktop:flex flex-col gap-4">
            {data.map((appointment) => (
              <PastAppointmentMobileView
                key={appointment.id}
                {...appointment}
                locale={locale}
              />
            ))}
          </div>

          <div
            className={twMerge(
              'desktop:px-7 bg-white rounded-xl flex grow justify-center items-center',
              (!!data.length || dataLoading) && 'hidden',
            )}
          >
            <EmptyStateComponent
              className="desktop:min-h-fit desktop:mt-24 desktop:-mb-40"
              titleClassName="font-exo"
              title={locale.noFind}
              text={locale.noFindDescription}
              testID={'past-appointments-empty'}
            />
          </div>

          <div className="desktop:min-h-[380px]">
            <TableContent
              columnKeys={columns as unknown as string[]}
              data={data}
              keyMap={keyMap}
              excludeSort={excludeSort}
              onColumnSortChange={handleSortChange}
              orderBy={historicAppointmentsOrderBy}
              customClasses={customClassesDesktop}
              locale={locale}
              theadClassName="bg-table-header"
              onRowClick={handleRowClick}
            />
          </div>
        </div>

        {hasShowPaginate && !dataLoading && (
          <div className="flex max-desktop:flex-col gap-4 justify-between items-center mt-7">
            <p className="text-sm font-medium text-med-gray-3">
              {paginateSubtitle}
            </p>
            <PaginateBlock
              itemsPerPage={variables.limit}
              totalItems={total}
              setNewOffset={setNewOffset}
              offsetParams={appointmentOffsetParams}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PastAppointmentWrapper;
